import type { FC } from 'react';
import * as React from 'react';
import { useMemo } from 'react';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { getGlobalServiceProviderIdFromToken } from '../../utils/getGlobalServiceProviderIdFromToken';
import { useGetServiceProviderStripeStatePhoenix } from '@pflegenavi/service-provider-frontend/api';
import {
  Divider,
  Grid,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { StripeStateCard } from './StripeStateCard';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface StripeStateOverviewProps {}

export const StripeStateOverview: FC<StripeStateOverviewProps> = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  // @ts-expect-error // getTokenImmediate is intentionally hidden. It is necessary here to extract the globalServiceProviderId
  const { getTokenImmediate } = useAuthentication();
  const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
    getTokenImmediate()
  );
  const { data } = useGetServiceProviderStripeStatePhoenix({
    globalServiceProviderId,
  });

  const { total, available, availableSoon, payouts, percentages, tooltips } =
    useMemo(() => {
      const total = data?.balance.total_balance_in_cents || 0;
      const available = data?.balance.available_amount_in_cents || 0;
      const availableSoon = data?.balance.available_soon_amount_in_cents || 0;
      const payouts = data?.balance.payout_amount_in_cents || 0;

      const percentages = {
        available: (available / total) * 100,
        availableSoon: (availableSoon / total) * 100,
        payouts: (payouts / total) * 100,
        total: 100,
      };

      const tooltips = {
        available: t('sp-web-app.stripe.tooltips.available-balance'),
        availableSoon: t('sp-web-app.stripe.tooltips.available-soon-balance'),
        payouts: t('sp-web-app.stripe.tooltips.payouts'),
        total: t('sp-web-app.stripe.tooltips.total-amount'),
      };

      return {
        total,
        available,
        availableSoon,
        payouts,
        percentages,
        tooltips,
      };
    }, [t, data]);

  if (!data) {
    return null;
  }

  return (
    <Stack>
      <Typography variant="h6">{t('sp-web-app.stripe.overview')}</Typography>
      <CardsContainer container spacing={2}>
        <Grid item xs={6} md={3} lg>
          <StripeStateCard
            title={t('sp-web-app.stripe.total-amount')}
            total={total}
            percent={percentages.total}
            amount={total}
            icon="tdesign:money"
            color={theme.palette.info.main}
            tooltipText={tooltips.total}
          />
        </Grid>

        <Grid item xs={6} md={3} lg>
          <StripeStateCard
            title={t('sp-web-app.stripe.available-soon-balance')}
            total={total}
            percent={percentages.availableSoon}
            amount={availableSoon}
            icon="bi:hourglass-split"
            color={theme.palette.warning.main}
            tooltipText={tooltips.availableSoon}
          />
        </Grid>

        <Grid item xs={6} md={3} lg sx={{ position: 'relative' }}>
          <StripeStateCard
            title={t('sp-web-app.stripe.available-balance')}
            total={total}
            percent={percentages.available}
            amount={available}
            icon="tabler:coin-euro-filled"
            color={theme.palette.success.main}
            tooltipText={tooltips.available}
          />
        </Grid>
        <Grid item xs={0} md={1} lg={0.1}>
          <Divider orientation="vertical" />
        </Grid>

        <Grid item xs={6} md={3} lg>
          <StripeStateCard
            title={t('sp-web-app.stripe.payouts')}
            total={total}
            percent={percentages.payouts}
            amount={payouts}
            icon="ic:baseline-move-to-inbox"
            color={theme.palette.secondary.dark}
            tooltipText={tooltips.payouts}
          />
        </Grid>
      </CardsContainer>
    </Stack>
  );
};

const CardsContainer = styled(Grid)(({ theme }) =>
  theme.unstable_sx({
    paddingY: 2,
    width: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  })
);
